/**
 * Szene des bewegten Titelbereichs (Verwaltung › Erscheinungsbild): das gepflegte Gebiet als
 * Modell. Grenzen aus VG250, je Gemeinde ein Ort aus kleinen Gebaeuden um ihren Mittelpunkt und ein
 * Messpunkt mit ihrem Namen. Alles in Einheitskoordinaten (u nach Osten, v nach Sueden, 0..1, Seiten
 * im richtigen Verhaeltnis); die Komponente legt es isometrisch auf die Flaeche.
 */
import type { TenantGebiet } from './tenant.js';

export interface SceneBuilding {
  u: number;
  v: number;
  /** Kantenlaenge der Grundflaeche (Einheitskoordinaten). */
  size: number;
  /** Hoehe im Verhaeltnis zur Kantenlaenge. */
  height: number;
  /** Sekunden bis zum Aufwachsen. */
  delay: number;
}

export interface ScenePin {
  name: string;
  u: number;
  v: number;
}

export interface HeroScene {
  /** Grenzen als Ringe (je Gemeinde ein oder mehrere). */
  rings: [number, number][][];
  buildings: SceneBuilding[];
  pins: ScenePin[];
}

/** Gleichmaessige Pseudozufallszahlen: dieselbe Gemeinde sieht bei jedem Besuch gleich aus. */
function random(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

export function insideRings(rings: [number, number][][], u: number, v: number): boolean {
  let inside = false;
  for (const r of rings) {
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      const [ui, vi] = r[i];
      const [uj, vj] = r[j];
      if (vi > v !== vj > v && u < ((uj - ui) * (v - vi)) / (vj - vi) + ui) inside = !inside;
    }
  }
  return inside;
}

/** Gebaeude je Gemeinde ungefaehr nach Flaeche, mindestens 10, zusammen hoechstens `total`. */
export function buildHeroScene(gebiete: TenantGebiet[], total = 90): HeroScene {
  if (gebiete.length === 0) return { rings: [], buildings: [], pins: [] };
  const west = Math.min(...gebiete.map((g) => g.bbox[0]));
  const south = Math.min(...gebiete.map((g) => g.bbox[1]));
  const east = Math.max(...gebiete.map((g) => g.bbox[2]));
  const north = Math.max(...gebiete.map((g) => g.bbox[3]));
  const k = Math.cos((((south + north) / 2) * Math.PI) / 180);
  const span = Math.max((east - west) * k, north - south, 1e-9);
  const uv = ([lon, lat]: number[]): [number, number] => [((lon - west) * k) / span, (north - lat) / span];

  const perGemeinde = gebiete.map((g) => {
    const polys = (g.geometry.type === 'Polygon' ? [g.geometry.coordinates] : g.geometry.coordinates) as number[][][][];
    // Nur Aussenringe (Loecher spielen fuer das Modell keine Rolle), ausgeduennt auf hoechstens 400 Punkte.
    const rings = polys.map((p) => {
      const step = Math.max(1, Math.ceil(p[0].length / 400));
      return p[0].filter((_, i) => i % step === 0).map(uv);
    });
    const [bw, bs, be, bn] = g.bbox;
    const area = (be - bw) * k * (bn - bs);
    return { g, rings, area };
  });
  const areaSum = perGemeinde.reduce((s, x) => s + x.area, 0) || 1;

  const buildings: SceneBuilding[] = [];
  const pins: ScenePin[] = [];
  for (const { g, rings, area } of perGemeinde) {
    const rnd = random(hash(g.ags));
    const [cu, cv] = uv([g.center.lon, g.center.lat]);
    // Mittelpunkt kann bei verwinkelten Gemeinden ausserhalb liegen; dann der erste Punkt innen.
    let pu = cu;
    let pv = cv;
    if (!insideRings(rings, pu, pv)) {
      for (let i = 0; i < 200; i++) {
        const u = cu + (rnd() - 0.5) * 0.2;
        const v = cv + (rnd() - 0.5) * 0.2;
        if (insideRings(rings, u, v)) {
          pu = u;
          pv = v;
          break;
        }
      }
    }
    pins.push({ name: g.name, u: pu, v: pv });
    const count = Math.max(10, Math.round((total * area) / areaSum));
    const spread = 0.035 + 0.04 * Math.sqrt(area / areaSum);
    for (let i = 0, tries = 0; i < count && tries < count * 40; tries++) {
      // Dichter Ortskern, nach aussen lockerer (Box-Muller).
      const r = Math.sqrt(-2 * Math.log(Math.max(rnd(), 1e-9))) * spread * 0.6;
      const a = rnd() * Math.PI * 2;
      const u = pu + Math.cos(a) * r;
      const v = pv + Math.sin(a) * r;
      if (!insideRings(rings, u, v)) continue;
      const near = Math.hypot(u - pu, v - pv) / spread;
      buildings.push({
        u,
        v,
        size: 0.009 + rnd() * 0.007,
        height: 0.6 + rnd() * (near < 0.5 && rnd() > 0.7 ? 2.4 : 1.1),
        delay: 0.6 + near * 1.2 + rnd() * 0.8,
      });
      i++;
    }
  }
  return { rings: perGemeinde.flatMap((x) => x.rings), buildings, pins };
}
